import {
  getUpdateLineupsPlayerPayload as _getUpdateLineupsPlayerPayload,
  LineupConfig as Config,
  filterPosition,
  getStat,
  isCaptain,
  isPlayerSame,
  LineupModel,
  toSortedBySurname,
  updateLineups,
} from '@collector/desktop-feature-lineups'
import { Events } from '@collector/sportsapi-client'
import {
  ParticipantSubType,
  SoccerLineupPositionId,
  SoccerSubParticipantStatId,
  SubParticipantPosition,
} from '@collector/sportsapi-client-legacy'
import { Event, Participant, SubParticipant } from '@collector/sportsapi-types'
import { sportsApiClient } from '@desktop/globalState/sportsApiClient'

import { LineupConfig } from '../../configuration'

export type PositionKey =
  'goalkeeper' | 'firstLineup' | 'bench' | 'coach' | 'willNotPlay'
export type SoccerLineupModel = LineupModel<PositionKey>
export type SoccerLineupConfig = Config<PositionKey>

function toSortedByPosition(
  subParticipants: SubParticipant[],
): SubParticipant[] {
  const positionOrder = {
    [SoccerLineupPositionId.Goalkeeper]: 0,
    [SoccerLineupPositionId.Defender]: 1,
    [SoccerLineupPositionId.Midfielder]: 2,
    [SoccerLineupPositionId.Attacker]: 3,
    [SoccerLineupPositionId.Unknown]: 4,
  }

  function getOrder(participant: SubParticipant): number {
    const positionId = participant.details?.position_id
    if (positionId) return positionOrder[positionId]
    else return 5
  }

  return [...subParticipants].sort((a, b) => getOrder(a) - getOrder(b))
}

function getUpdateLineupsPlayerPayload(
  players: SubParticipant[],
  position: SubParticipantPosition,
  forcedPlayerPosition?: number,
): Events.SubParticipantsSave.SubParticipantsSavePostSquad[] {
  return players.map((player) => {
    let playerPosition = forcedPlayerPosition
    const playerPositionStat = getStat(
      player,
      SoccerSubParticipantStatId.PPosition,
    )
    if (playerPositionStat?.value) playerPosition ??= +playerPositionStat.value

    return {
      ..._getUpdateLineupsPlayerPayload(player, position),
      player_position: playerPosition,
    }
  })
}

function hasPositionChanged(
  key: PropertyKey,
  lineup: LineupModel,
  initialLineup: LineupModel,
): boolean {
  const current = lineup[key]
  const initial = initialLineup[key]

  if (current.length !== initial.length) return true

  for (let i = 0; i < current.length; i++) {
    const playerA: SubParticipant = current[i]
    const playerB: SubParticipant = initial[i]

    const samePlayer = isPlayerSame(playerA, playerB)
    const sameCaptain = isCaptain(playerA) === isCaptain(playerB)

    const sameShirtNr = playerA.shirt_nr === playerB.shirt_nr
    const same = samePlayer && sameCaptain && sameShirtNr
    if (!same) return true
  }

  return false
}

export const lineupConfig: LineupConfig = {
  config: {
    positions: {
      goalkeeper: {
        mergeWith: 'firstLineup',
        rowPrefix: '(GK)',
        showCaptainRadio: true,
        sectionLabel: '',
        showShirtNr: true,
        maxCount: 1,
        maxCountReasonText: 'Goalkeeper is already added to first lineup',
        buttonLabel: 'GK',
        buttonClass:
          'hover:bg-success-8 hover:border-success-8 active:bg-success-8/50',
        addFoundPlayer: {
          confirmMissingShirtNr: true,
          disableForSubTypes: [ParticipantSubType.Coach],
        },
      },
      firstLineup: {
        sectionLabel: '1st lineup',
        showCaptainRadio: true,
        showShirtNr: true,
        maxCount: 10,
        maxCountReasonText: 'First lineup already has 11 players',
        buttonLabel: '1st',
        buttonClass:
          'hover:bg-neutral-9 hover:border-neutral-9 active:bg-neutral-9/50',
        addFoundPlayer: {
          confirmMissingShirtNr: true,
          disableForSubTypes: [ParticipantSubType.Coach],
        },
      },
      bench: {
        sectionLabel: 'Bench',
        showShirtNr: true,
        buttonLabel: 'BEN',
        buttonClass: 'hover:bg-neutral-light-6 hover:border-neutral-dark-0',
        addFoundPlayer: {
          confirmMissingShirtNr: true,
          disableForSubTypes: [ParticipantSubType.Coach],
        },
      },
      coach: {
        sectionLabel: 'Coach',
        maxCount: 1,
        maxCountReasonText: 'Coach is already added',
        buttonLabel: 'COA',
        buttonClass: 'hover:bg-neutral-light-6 hover:border-neutral-dark-0',
      },
      willNotPlay: {
        deleteOnly: true,
        sectionLabel: 'Will not play',
        buttonLabel: '',
        buttonClass: 'hover:bg-neutral-light-6 hover:border-neutral-dark-0',
      },
    },
  },
  createLineupModel(players: SubParticipant[]): SoccerLineupModel {
    return {
      goalkeeper: filterPosition(players, SubParticipantPosition.Goalkeeper),
      firstLineup: filterPosition(players, SubParticipantPosition.FirstLineup),
      bench: filterPosition(players, SubParticipantPosition.Bench),
      coach: filterPosition(players, SubParticipantPosition.Coach),
      willNotPlay: filterPosition(players, SubParticipantPosition.WillNotPlay),
    }
  },
  toSortedLineupModel(lineup: LineupModel<PositionKey>): SoccerLineupModel {
    return {
      goalkeeper: toSortedByPosition(toSortedBySurname(lineup.goalkeeper)),
      firstLineup: toSortedByPosition(toSortedBySurname(lineup.firstLineup)),
      bench: [...lineup.bench],
      coach: [...lineup.coach],
      willNotPlay: [...lineup.willNotPlay],
    }
  },
  async updateLineups(
    eventId: Event['id'],
    participantId: Participant['id'],
    lineup: LineupModel<PositionKey>,
  ): Promise<void> {
    // ?NOTE: force goalkeeper to be at position 1
    // scenario:
    // has_formations=soon or has_formations=yes
    // 1. players in formation are set to positions, except goalkeeper (not in lineup yet)
    // 2. user adds goalkeeper to lineup and saves squad
    // 3. opening formation modal must show goalkeeper at position 1

    const squad: Events.SubParticipantsSave.SubParticipantsSavePostSquad[] = [
      ...getUpdateLineupsPlayerPayload(
        lineup.goalkeeper,
        SubParticipantPosition.Goalkeeper,
        1,
      ),
      ...getUpdateLineupsPlayerPayload(
        lineup.firstLineup,
        SubParticipantPosition.FirstLineup,
      ),
      ...getUpdateLineupsPlayerPayload(
        lineup.bench,
        SubParticipantPosition.Bench,
      ),
      ...getUpdateLineupsPlayerPayload(
        lineup.coach,
        SubParticipantPosition.Coach,
      ),
      ...getUpdateLineupsPlayerPayload(
        lineup.willNotPlay,
        SubParticipantPosition.WillNotPlay,
      ),
    ]

    await updateLineups(sportsApiClient, eventId, participantId, squad)
  },
  hasPositionChanged,
}
